import { createContext, useState, useCallback, useMemo } from 'react'
import { Filters, TagRef } from '../types'
import { filterLink } from '../lib/filter_link'
import { categoryPath } from '../lib/browse_params'

const defaultFilters: Filters = {
  selectedGame: undefined,
  selectedCategories: [],
  selectedCardTypes: [],
  selectedEnergyTypes: [],
  selectedView: 'listings',
  selectedRarities: [],
  priceRange: {},
  selectedConditions: [],
  selectedStocks: [],
}

const defaultSearchBoxPlaceholder = ''

const SearchContext = createContext<{
  searchString: string
  setSearchString?: (s: string) => void
  setFilters?: (filters: Filters) => void
  defaultFilters: Filters
  filters: Filters
  filterMenuDisplayed: boolean
  setFilterMenuDisplayed?: (s: boolean) => void
  hasFilters: boolean
  cachedTagRefs: TagRef[]
  setCachedTagRefs?: (refs: TagRef[]) => void
  appendCachedTagRefs?: (refs: TagRef[]) => void
  pendingInitialFilterCategories: number[]
  setPendingInitialFilterCategories?: (tagIds: number[]) => void
  endpointForExistingFilters?: (f: Filters, query: string) => string
  categoriesLink?: (tid?: string, page?: number) => string
  searchboxPlaceholder: string
  setSearchboxPlaceholder?: (text?: string) => void
  isUserProfile: boolean
  setIsUserProfile?: (value: boolean) => void
}>({
      filterMenuDisplayed: false,
      searchString: '',
      defaultFilters: defaultFilters,
      filters: defaultFilters,
      hasFilters: false,
      cachedTagRefs: [],
      pendingInitialFilterCategories: [],
      searchboxPlaceholder: defaultSearchBoxPlaceholder,
      isUserProfile: false,
    })

const SearchProvider = ({ children }) => {
  const [searchString, _setSearchString] = useState<string>('')
  const [filters, _setFilters] = useState<Filters>(defaultFilters)
  const [filterMenuDisplayed, _setFilterMenuDisplayed] = useState<boolean>(false)
  const [cachedTagRefs, _setCachedTagRefs] = useState<TagRef[]>([])
  const [pendingInitialFilterCategories, _setPendingInitialFilterCategories] = useState<number[]>([])
  const [searchboxPlaceholder, _setSearchboxPlaceholder] = useState<string>(defaultSearchBoxPlaceholder)
  const [isUserProfile, _setIsUserProfile] = useState<boolean>(false)

  const setSearchString = useCallback((s: string) => {
    _setSearchString(s)
  }, [])

  const setFilters = useCallback((filters: Filters) => {
    _setFilters(filters)
  }, [])

  const setFilterMenuDisplayed = useCallback((s: boolean) => {
    _setFilterMenuDisplayed(s)
  }, [])

  const setCachedTagRefs = useCallback((refs: TagRef[]) => {
    _setCachedTagRefs(refs)
  }, [])

  const appendCachedTagRefs = useCallback((refs: TagRef[]) => {
    _setCachedTagRefs(c => {
      const r = refs.filter(workingRef => !c.find(c => c.id === workingRef.id))
      return c.concat(r)
    })
  }, [])

  const setPendingInitialFilterCategories = useCallback((tagIds: number[]) => {
    _setPendingInitialFilterCategories(tagIds)
  }, [])

  const endpointForExistingFilters = useCallback((f: Filters, query: string) => {
    let p = '?'

    p += filterLink(
      f.selectedView,
      f.selectedCardTypes,
      f.selectedEnergyTypes,
      f.selectedRarities,
      f.selectedConditions,
      f.selectedStocks,
      f.priceRange.min,
      f.priceRange.max,
      f.selectedUserId,
      query,
      f.selectedGame,
    )

    return p
  }, [])

  const categoriesLink = useCallback((tid?: string, page?: number) => {
    const f: Filters = {
      ...filters,
      selectedCategories: [],
    }

    return categoryPath(tid, endpointForExistingFilters(f, searchString), window.location.search, page)
  }, [endpointForExistingFilters, filters, searchString])

  const setSearchboxPlaceholder = useCallback((value?: string) => {
    _setSearchboxPlaceholder(value ?? defaultSearchBoxPlaceholder)
  }, [])

  const setIsUserProfile = useCallback((value: boolean) => {
    _setIsUserProfile(value)
  }, [])

  const hasFilters = useMemo(() => {
    const stockFilterActive = filters.selectedStocks.length > 1 || (filters.selectedStocks.length === 1 && filters.selectedStocks[0] !== 'for_sale')
    return (
      !!filters.priceRange.min ||
      !!filters.priceRange.max ||
      filters.selectedCardTypes.length > 0 ||
      filters.selectedConditions.length > 0 ||
      filters.selectedEnergyTypes.length > 0 ||
      filters.selectedRarities.length > 0 ||
      stockFilterActive ||
      searchString !== '' ||
      !!filters.selectedGame
    )
  }, [filters.priceRange.max, filters.priceRange.min, filters.selectedCardTypes.length, filters.selectedConditions.length, filters.selectedEnergyTypes.length, filters.selectedGame, filters.selectedRarities.length, filters.selectedStocks, searchString])

  return (
    <SearchContext.Provider value={ { searchString, setSearchString, defaultFilters, filters, setFilters, filterMenuDisplayed, setFilterMenuDisplayed, hasFilters, cachedTagRefs, setCachedTagRefs, appendCachedTagRefs, pendingInitialFilterCategories, setPendingInitialFilterCategories, endpointForExistingFilters, categoriesLink, searchboxPlaceholder, setSearchboxPlaceholder, isUserProfile, setIsUserProfile } }>
      { children }
    </SearchContext.Provider>
  )
}

export { SearchProvider, SearchContext }
